import { ANetResponse } from "../purchase/authorize/authorize.types";
import { MarketingReferral } from "./common.types";

interface AccountInfo {
  email: string;
  dietaryPreferences: ArrayOptionsItem[];
  firstName: string;
  lastName: string;
  noteDriver?: string;
  password: string;
  phone: string;
  birthday?: string;
  referredBy?: string | null;
}

interface Address {
  additional?: string;
  city?: string;
  latitude?: number;
  longitude?: number;
  stateCode?: string;
  street?: string;
  zip?: string;
  noteDriver?: string;
  zoneId?: number;
}

interface Referral {
  source: string;
  additionalInfo: string;
}

interface Settings {
  receiveNewsletter: boolean;
  receiveText: boolean;
  receiveTextDelivery: boolean;
  referral?: Referral;
  delivery: AccountDeliveryInfo
}

export interface AccountDeliveryInfo {
  type: number;
  dayId: number;
  timeWindowId?: number
}

interface Contact {
  firstName: string,
  lastName: string,
  email: string,
  phone: string
}

export interface NewAccountPayload {
  /** Proof the address was verified through the emailed link. The backend
   *  rejects account creation when it is missing or does not match
   *  `accountInfo.email` — without that check the whole step is decorative. */
  verificationToken?: string;
  turnstileToken?: string;
  accountInfo: AccountInfo;
  address?: Address;
  settings: Settings;
  contact?: Contact;
  marketingReferralData?: MarketingReferral;
}


// DELIVERY OPTIONS
export interface DeliveryOptionsResponseItem extends ArrayOptionsItem {
  allowTimeWindow: boolean,
  timeSet: ArrayOptionsItem[];
  isThisWeek: boolean;
  formattedName?: string;
  deliveryDate?: string;
}

// ACCOUNT SETTINGS:
export interface ArrayOptionsItem {
  id: string | number | null;
  name: string;
}

// PICKUP OPTION
export interface PickUpOption {
  id: number;
  name: string | false;
  deliveryDays: DeliveryDay[];
  locationAddress: string;
  composedName?: string;
  additionalInfo?: string;
}

export interface DeliveryDay {
  id: number;
  name: string;
  deliveryWindow: string;
  cutOffDate: string;
}

export interface PeopleShoppingFor extends ArrayOptionsItem {
  img: string;
}

export interface AccountSettings {
  references: Reference[];
  dietaryPreferences: ArrayOptionsItem[];
  peopleShoppingFor?: PeopleShoppingFor[];
}

export interface Reference extends ArrayOptionsItem {
  hasAdditionalInfo: boolean;
}

export interface DetailedCard extends Card {
  securityCode?: string;
  expirationDate?: string;
}

interface BillingAddress {
  street?: string;
  additional?: string;
  city?: string;
  stateCode?: string;
  zip?: string;
}

export interface PaymentDataPayload {
  turnstileToken?: string;
  card: DetailedCard;
  billingAddress?: BillingAddress;
  opaqueData?: ANetResponse
}

interface Card {
  firstName: string
  lastName: string,
  number: string
}

export interface PaymentMethodResponse {
  card: Card,
  settings: {
    hasMembership: boolean;
    hasPaymentMethod: boolean;
    isSuspended: boolean;
    isLimited: boolean;
    requireAddress: boolean;
    requirePaymentMethod: boolean;
    firstLogin: boolean;
    isAbandoned: boolean;
    isExplorer?: boolean;
    isAnonymousExplorer?: boolean;
  }
}

//#region Explorer flow (DEV-11262)

/**
 * Body for `POST /explorer` — creates the explorer account and returns a
 * session. It does *not* create an order: the cart is born with the first
 * `POST /order`, exactly as it is for a customer, and joins that week's
 * active order if there already is one.
 *
 * This is where Pet Wants departs from Á Table. Á Table only delivers, so its
 * payload is just `{ email, address }`. Pet Wants also picks up, and the
 * backend reads the fulfilment choice off `pickUpOptionId`:
 *  - sent     → pickup branch, no address;
 *  - omitted  → home delivery, address required.
 *
 * The branch and the membership have to agree. Home delivery with a type that
 * picks up answers 400 `This customer type picks up: send pickUpOptionId`.
 */
export interface ExplorerUserPayload {
  email: string;
  /** Home delivery only. Resuming replaces it, and it is never handed back,
   *  which is why the resume flow has to ask for it again. */
  address?: ExplorerAddressPayload;
  /** `id` from `GET /customer-types`. Mandatory on the pickup branch, where
   *  the locations hang off the membership. Optional on home delivery, where
   *  it used to be ignored and now decides the level the explorer is born
   *  with — so send what the visitor actually chose. */
  customerTypeId?: number;
  /** Pickup only — a location of that membership, from
   *  `GET /account/pickup-locations?membershipId=`. */
  pickUpOptionId?: number;
  /** Mandatory on both branches. Home delivery: a day of the address's zone,
   *  from `GET /account/delivery-options?zoneId=`. Pickup: one of the
   *  location's own `deliveryDays`. It comes back in the session as
   *  `deliveryInfo.deliveryDayId`, and moving it later makes the backend
   *  cancel the carts. */
  settings: { delivery: { dayId: number } };
  /** The campaign that brought them in. Attribution has to travel on the
   *  call that creates the account; nothing later carries it. */
  marketingReferralData?: MarketingReferral;
  turnstileToken?: string;
}

export interface ExplorerAddressPayload {
  street: string;
  additional?: string;
  city: string;
  stateCode: string;
  zip: string;
  latitude: number;
  longitude: number;
  zoneId: number;
}

/** Shape returned by `POST /check-email` when the address belongs to an
 *  explorer, so a half-finished registration can be recovered. */
export interface EmailExistenceResponse {
  emailUsed: boolean;
  /** True when the email belongs to an explorer who has not registered yet.
   *  Note it is only a flag: the backend deliberately returns nothing about
   *  the account or its address, because resuming replaces the address with
   *  whatever the next call sends rather than restoring the old one. */
  isAnonymousExplorer?: boolean;
}

//#endregion

export interface CustomerType extends ArrayOptionsItem {
  configuration?: {
    requireAddress: boolean;
    requirePaymentMethod: boolean;
  }
}


//#region Memebserhip permissions for eCommerce:
export enum PermissionNames {
  order = 'order',
  shop = 'shop',
  settings = 'settings',
  settingsFriednReferrals = 'settings.friendReferral',
  settingsProfile = 'settings.profile',
  settingsSubscription = 'settings.subscriptions',
  settingsDeliveries = 'settings.deliveries',
  settingsPaymentMethod = 'settings.paymentMethod',
  settingsMemebership = 'settings.membership',
  settingsOrderHistory = 'settings.orderHistory',
  settingsInvoiceHistory = 'settings.invoiceHistory',
};

export interface permission {
  allowed: boolean;
  eCommRoute: string;
}

export interface eCommercePermissions {
  pages: permission;
  home: permission;
  buyAgain: permission;
  favorites: permission;
  cart: permission;
  order: permission;
  shop: permission;
  settings: {
    friendReferral: permission;
    profile: permission;
    subscriptions: permission;
    deliveries: permission;
    paymentMethod: permission;
    membership: permission;
    orderHistory: permission;
    invoiceHistory: permission;
  }
}

export const normalizeECommercePermissionsResponse = (data?: any): eCommercePermissions => {
  return {
    pages: {
      allowed: true,
      eCommRoute: '/pages'
    },
    home: {
      allowed: true,
      eCommRoute: '/'
    },
    buyAgain: {
      allowed: data?.shop ?? false,
      eCommRoute: '/buy-again'
    },
    cart: {
      allowed: data?.shop ?? false,
      eCommRoute: '/cart'
    },
    favorites: {
      allowed: data?.shop ?? false,
      eCommRoute: '/favorites'
    },
    order: {
      allowed: data?.order ?? false,
      eCommRoute: '/order'
    },
    shop: {
      allowed: data?.shop ?? false,
      eCommRoute: '/shop'
    },
    settings: {
      friendReferral: {
        allowed: data?.settings?.friendReferral ?? false,
        eCommRoute: '/settings/account/friend-referrals'
      },
      profile: {
        allowed: data?.settings?.profile ?? false,
        eCommRoute: '/settings/account/profile'
      },
      subscriptions: {
        allowed: data?.settings?.subscriptions ?? false,
        eCommRoute: '/settings/account/subscriptions'
      },
      deliveries: {
        allowed: data?.settings?.deliveries ?? false,
        eCommRoute: '/settings/account/deliveries'
      },
      paymentMethod: {
        allowed: data?.settings?.paymentMethod ?? false,
        eCommRoute: '/settings/billing/payment-method'
      },
      membership: {
        allowed: data?.settings?.membership ?? false,
        eCommRoute: '/settings/billing/membership'
      },
      orderHistory: {
        allowed: data?.settings?.orderHistory ?? false,
        eCommRoute: '/settings/billing/order-and-billing'
      },
      invoiceHistory: {
        allowed: data?.settings?.invoiceHistory ?? false,
        eCommRoute: '/settings/billing/invoice-history'
      },
    }
  }
}
//#endregion

export interface RetentionCoupon {
  id: number;
  name: string;
  tier: number;
}
